import { useState, useRef, useEffect } from 'react';
import { Coins } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useBetaCredits } from '@/hooks/useBetaCredits';

const BetaCreditsDropdown = () => {
  const { totalCredits, isBetaUser, loading } = useBetaCredits();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (loading || !isBetaUser || totalCredits <= 0) return null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className={cn(
          "flex items-center justify-center h-9 w-9 rounded-md transition-colors",
          open ? "text-foreground bg-muted" : "text-amber-400 hover:text-amber-300 hover:bg-muted"
        )}
        title="Beta Credits"
      >
        <Coins className="h-5 w-5" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 rounded-lg border border-border bg-card shadow-lg z-50 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">🎉</span>
            <span className="text-sm font-semibold">Beta User Bonus</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Sabotage Credits</span>
            <Badge className="bg-success/20 text-success border-success/30">${totalCredits}</Badge>
          </div>

          <p className="text-[10px] text-muted-foreground/70">
            Credits apply automatically when purchasing sabotages during drafts or matchups.
          </p>
        </div>
      )}
    </div>
  );
};

export default BetaCreditsDropdown;
